import React, { useEffect, useState, useRef, lazy, Suspense } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardOverflow,
    Divider,
    Drawer,
    FormHelperText,
    Input,
    Link,
    LinearProgress,
    List,
    ListItemButton,
    ListItemContent,
    ModalClose,
    Option,
    Select,
    Tooltip,
    Typography,
} from "@mui/joy";
import {
    ArrowRightAltRounded,
    Home,
    LocationOn,
    Person2,
    Email,
    Phone,
    Savings,
    CalendarMonth, // New icon for timeline
} from "@mui/icons-material";
import { GetAddressFromPostcode, SubmitLeadForm } from "../../../Api";
import { useStatsigClient } from "@statsig/react-bindings";
import {useSearchParams} from "react-router-dom";

const BookingModal = lazy(() => import("../../modals/BookingModal"));

// --- Mobile-Only Components & Hook ---

// Custom hook to detect mobile screen sizes
const useIsMobile = (breakpoint = 768) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < breakpoint);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);

    return isMobile;
};


// Helper component for formatting a single address in the mobile list
const AddressItem = ({ fullAddress, onSelect }) => {
    const parts = fullAddress.split(",").map((p) => p.trim());
    const firstLine = parts[0] || "";
    const restOfAddress = parts.slice(1).join(", ");

    return (
        <ListItemButton onClick={() => onSelect(fullAddress)}>
            <ListItemContent>
                <Typography level="title-md">{firstLine}</Typography>
                <Typography level="body-sm">{restOfAddress}</Typography>
            </ListItemContent>
        </ListItemButton>
    );
};

// Mobile-friendly bottom sheet for address selection
const AddressSheet = ({ open, onClose, addresses, onSelect }) => {
    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={onClose}
            sx={{
                "& .MuiDrawer-paper": {
                    borderTopLeftRadius: "16px",
                    borderTopRightRadius: "16px",
                    maxHeight: "70vh",
                    mx: "auto",
                    maxWidth: "600px",
                },
            }}
        >
            <ModalClose />
            <Typography level="title-lg" sx={{ p: 2 }}>
                Select Your Address
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <List sx={{
                overflowY: 'scroll',
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'var(--joy-palette-neutral-300)',
                    borderRadius: '8px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: 'var(--joy-palette-neutral-400)',
                },
            }}>
                {addresses.map((address, index) => (
                    <AddressItem
                        key={index}
                        fullAddress={address.address}
                        onSelect={onSelect}
                    />
                ))}
            </List>
        </Drawer>
    );
};


// --- Main Form Component ---

interface AddressResponse {
    suggestions: Address[];
}

interface Address {
    address: string;
}

const formatUkPhoneNumber = (value: string) => {
    const rawValue = value.trim();
    const digits = rawValue.replace(/\D/g, "");

    let nationalNumber = digits;
    if (digits.startsWith("0044")) {
        nationalNumber = `0${digits.slice(4)}`;
    } else if (digits.startsWith("44")) {
        nationalNumber = `0${digits.slice(2)}`;
    } else if (digits.startsWith("7") && digits.length === 10) {
        nationalNumber = `0${digits}`;
    }

    if (nationalNumber.startsWith("07") && nationalNumber.length === 11) {
        return `${nationalNumber.slice(0, 5)} ${nationalNumber.slice(5)}`;
    }

    return nationalNumber;
};

const isValidUkPhoneNumber = (value: string) => {
    const formattedNumber = formatUkPhoneNumber(value);
    const digits = formattedNumber.replace(/\D/g, "");

    return (
        digits.startsWith("0") &&
        (digits.length === 10 || digits.length === 11) &&
        ["1", "2", "3", "7", "8"].includes(digits[1])
    );
};

const isUuid = (value: string | null) =>
    Boolean(value && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value));

export default function MultiRoofForm({ franchise }) {
    const isMobile = useIsMobile();
    const [searchParams] = useSearchParams();
    const [fbclid, setFbclid] = useState<string | null>(null);
    const [campaignName, setCampaignName] = useState<string | null>(null);

    // Determine total steps based on fbclid
    const totalSteps = fbclid ? 4 : 3;

    const [step, setStep] = useState(1);
    const [lead, setLead] = useState({
        name: "",
        eMail: "",
        phone: "",
        houseType: "Detached",
        postcode: "",
        address: "",
        timeline: "", // <-- NEW: State for the timeline
        roofCleanReason: "",
    });
    const [validPostcode, setValidPostcode] = useState(true);
    const [addressList, setAddressList] = useState<Address[]>([]);
    const [loadingPostcode, setLoadingPostcode] = useState(false);
    const [loadingBookingsPage, setLoadingBookingsPage] = useState(false);
    const [isAddressSelectOpen, setIsAddressSelectOpen] = useState(false);
    const [isAddressSheetOpen, setIsAddressSheetOpen] = useState(false);
    const selectedService = "Roof Clean";
    const { logEvent } = useStatsigClient();

    const [postcodeError, setPostcodeError] = useState("");
    const [detailsError, setDetailsError] = useState("");
    const [phoneError, setPhoneError] = useState("");
    const [validatedPostcodeQuery, setValidatedPostcodeQuery] = useState<string>("");
    const wantsToProceedRef = useRef(false);

    const [openBookingModal, setOpenBookingModal] = useState(false);
    const [longLeadId, setLeadLongId] = useState("");

    const nextStep = () => {
        if (step === 1) {
            const currentPostcode = lead.postcode.trim();
            if (currentPostcode === "") {
                setPostcodeError("Please enter your postcode.");
                wantsToProceedRef.current = false;
                return;
            }
            if (loadingPostcode) {
                wantsToProceedRef.current = true;
                return;
            }
            if (validatedPostcodeQuery === currentPostcode) {
                if (validPostcode) {
                    setPostcodeError("");
                    setStep((prev) => prev + 1);
                    wantsToProceedRef.current = false;
                } else {
                    setPostcodeError(postcodeError || "Please enter a valid postcode that returns addresses.");
                    wantsToProceedRef.current = false;
                }
            } else {
                wantsToProceedRef.current = true;
            }
            return;
        } else if (step === 2) {
            const currentAddress = lead.address || "";
            const addressMissing = currentAddress.trim() === "";
            if (addressMissing) {
                setDetailsError("Please select your address.");
                return;
            }
            setDetailsError("");
            setIsAddressSelectOpen(false);
            setStep((prev) => prev + 1);
        } else if (fbclid && step === 3) { // <-- NEW: Logic for the Facebook qualifying step
            if (!lead.timeline) {
                setDetailsError("Please select a timeframe to continue.");
                return;
            }
            if (!lead.roofCleanReason) {
                setDetailsError("Please select what made you request a roof clean.");
                return;
            }
            setDetailsError("");
            setStep((prev) => prev + 1); // Move to the final contact step
        }
    };

    function saveLead() {
        setLoadingBookingsPage(true);
        sendQuoteConversion();
        saveForm();
    }
    useEffect(() => {
        const fb = searchParams.get("fbclid");
        if (fb) {
            setFbclid(fb);
        }
        const utm_campaign = searchParams.get("utm_campaign") || searchParams.get("campaign_name") || searchParams.get("campaign");
        if (utm_campaign) {
            setCampaignName(utm_campaign);
        }
    }, [searchParams]);

    async function saveForm() {
        logEvent("lead_event");
        const formattedLead = {
            ...lead,
            phone: formatUkPhoneNumber(lead.phone),
        };
        let facebook_data = {};
        facebook_data["user_agent"] = navigator.userAgent;
        facebook_data["browser_id"] = getBrowserID();
        facebook_data["click_id"] = getClickID();
        if (campaignName) {
            facebook_data["campaign_name"] = campaignName;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const gclid = urlParams.get("gclid") || localStorage.getItem("gclid");
        if (gclid) {
            localStorage.setItem("gclid", gclid);
        }

        const msclkid = urlParams.get("msclkid") || localStorage.getItem("msclkid");
        if (msclkid) {
            localStorage.setItem("msclkid", msclkid);
        }
        const refParam = urlParams.get("ref");
        const referralCode = urlParams.get("referral") || (isUuid(refParam) ? null : refParam);

        // The 'lead' object now contains the 'timeline' field, so it will be sent automatically
        await SubmitLeadForm(
            formattedLead,
            selectedService,
            false,
            facebook_data,
            gclid,
            msclkid,
            referralCode as any
        ).then((r: any) => {
            setLeadLongId(r.long_form_id);
            setOpenBookingModal(true);
        });
    }
    function sendQuoteConversion() {
        window.gtag('event', 'conversion', {
            'send_to': 'AW-16693850742/qGn8CIu989EZEPbkn5g-',
            'value': 500,
            'currency': 'GBP'
        });

        (window as any).uetq = (window as any).uetq || [];
        (window as any).uetq.push("event", "submit_lead_form", {
            revenue_value: 500,
            currency: "GBP",
        });
    }

    function getClickID(): string | null {
        const match = document.cookie.match(/(^|;)\s*_fbc=([^;]+)/);
        return match ? match[2] : null;
    }

    function getBrowserID(): string | null {
        const fbp = document.cookie.match(/_fbp=([^;]+)/);
        return fbp ? fbp[1] : null;
    }

    useEffect(() => {
        const currentPostcodeTrimmed = lead.postcode.trim();
        if (currentPostcodeTrimmed === "") {
            setAddressList([]);
            setValidPostcode(true);
            setLoadingPostcode(false);
            if (postcodeError && postcodeError !== "Please enter your postcode.") {
                setPostcodeError("");
            }
            setValidatedPostcodeQuery("");
            return;
        }

        const handler = setTimeout(() => {
            const postcodeToValidate = lead.postcode.trim();
            if (postcodeToValidate === "") {
                setLoadingPostcode(false);
                return;
            }
            setLoadingPostcode(true);
            setPostcodeError("");
            const getAddresses = async () => {
                let isCurrentValidationSuccessful = false;
                let errorForThisValidation = "";
                try {
                    const response = (await GetAddressFromPostcode(postcodeToValidate)) as AddressResponse | null;
                    if (lead.postcode.trim() !== postcodeToValidate) {
                        return;
                    }
                    if (response && response.suggestions && response.suggestions.length > 0) {
                        setAddressList(response.suggestions);
                        isCurrentValidationSuccessful = true;
                    } else {
                        setAddressList([]);
                        isCurrentValidationSuccessful = false;
                        errorForThisValidation = "No addresses found for this postcode, or invalid postcode.";
                    }
                } catch (error) {
                    console.error("Failed to fetch addresses:", error);
                    if (lead.postcode.trim() !== postcodeToValidate) {
                        return;
                    }
                    setAddressList([]);
                    isCurrentValidationSuccessful = false;
                    errorForThisValidation = "Failed to validate postcode. Please try again.";
                } finally {
                    if (lead.postcode.trim() === postcodeToValidate) {
                        setLoadingPostcode(false);
                        setValidPostcode(isCurrentValidationSuccessful);
                        setPostcodeError(errorForThisValidation);
                        setValidatedPostcodeQuery(postcodeToValidate);
                        if (wantsToProceedRef.current) {
                            if (isCurrentValidationSuccessful) {
                                setPostcodeError("");
                                setStep((prev) => prev + 1);
                            }
                            wantsToProceedRef.current = false;
                        }
                    } else {
                        setLoadingPostcode(false);
                    }
                }
            };
            getAddresses();
        }, 750);

        return () => {
            clearTimeout(handler);
        };
    }, [lead.postcode]);

    useEffect(() => {
        if (step === 2 && addressList.length > 0) {
            const timer = setTimeout(() => {
                if (isMobile) {
                    setIsAddressSheetOpen(true);
                } else {
                    setIsAddressSelectOpen(true);
                }
            }, 100);
            return () => clearTimeout(timer);
        } else if (step !== 2) {
            setIsAddressSelectOpen(false);
            setIsAddressSheetOpen(false);
        }
    }, [step, addressList.length, isMobile]);


    return (
        <>
            {openBookingModal && (
                <Suspense fallback={<div>Loading Booking Options...</div>}>
                    <BookingModal
                        lead_id={longLeadId}
                        initOpen={openBookingModal}
                        franchise={franchise}
                        onClose={() => setOpenBookingModal(false)}
                    />
                </Suspense>
            )}

            <form
                className="form-width-2"
                id="form-submit"
                onSubmit={(event) => {
                    event.preventDefault();
                    if (step === totalSteps) { // <-- Use totalSteps for submission check
                        if (!lead.name || !lead.eMail || !lead.phone) {
                            alert("Please fill in all contact details.");
                            return;
                        }
                        if (!isValidUkPhoneNumber(lead.phone)) {
                            setPhoneError("Please enter a valid UK phone number.");
                            return;
                        }
                        saveLead();
                    }
                }}
            >
                <Card
                    color="primary"
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: '300px',
                        width: "80%",
                        maxWidth: "500px",
                        margin: "2rem auto",
                        borderRadius: "16px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                        overflow: "hidden",
                        gap: '16px',
                        border: "1px solid #E0E3E7",
                    }}
                >

                    <Box sx={{ mt: 2, textAlign: "center" }}>
                        <Typography
                            level="h1"
                            color={"primary"}
                            sx={{
                                fontSize: "26px",
                                fontWeight: 700,
                            }}
                        >
                            Get Your Roof Clean Price
                        </Typography>

                        {!(fbclid && step === 3) && (
                            <Typography
                                level="h2"
                                color={"primary"}
                                sx={{
                                    fontSize: "22px",
                                    fontWeight: 500,
                                }}
                            >
                                {/* NEW: Dynamic heading based on step */}
                                {step === totalSteps
                                    ? "Where should we send your quote?"
                                    : "Where are you located?"}
                            </Typography>
                        )}
                    </Box>

                    {step === 1 && (
                        <Box sx={{ px: '12px', display: "flex", flexDirection: "column", gap: 2 }}>
                            <Input
                                startDecorator={<LocationOn />} size="lg" sx={{ background: "#fff", fontSize: 16, height: 50 }}
                                required value={lead.postcode}
                                onChange={(e) => {
                                    setLead((prev) => ({ ...prev, postcode: e.target.value.toUpperCase() }));
                                    if (postcodeError) setPostcodeError("");
                                }}
                                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); nextStep(); } }}
                                placeholder="Enter Your Postcode" error={!!postcodeError && !validPostcode}
                            />
                            {(postcodeError || loadingPostcode) && (
                                <FormHelperText sx={{ color: postcodeError ? "danger.500" : "text.secondary" }}>
                                    {loadingPostcode ? "Validating postcode..." : postcodeError}
                                </FormHelperText>
                            )}
                            <Button color="success" onClick={nextStep} loading={loadingPostcode}
                                    sx={{ fontSize: '18px',  py: 1.5, borderRadius: 8, width: "100%", fontWeight: 600 }}>
                                Get Your Quote Now
                            </Button>
                        </Box>
                    )}

                    {step === 2 && (
                        <Box sx={{ px: '12px', display: "flex", flexDirection: "column", gap: 2 }}>
                            {isMobile ? (
                                <>
                                    <Tooltip title={addressList?.length > 0 ? "" : "Enter a valid postcode on the previous step."} arrow placement="bottom" variant="solid" color={addressList?.length > 0 ? undefined : "danger"}>
                                        <Button
                                            variant="soft" color="neutral" size="lg"
                                            disabled={addressList?.length === 0}
                                            onClick={() => setIsAddressSheetOpen(true)}
                                            startDecorator={<Home />}
                                            sx={{ justifyContent: 'flex-start', color: lead.address ? 'text.primary' : 'text.tertiary', background: "#fff", fontSize: 16, '&:hover': { background: '#f0f0f0' } }}
                                        >
                                            {lead.address || "Your Address..."}
                                        </Button>
                                    </Tooltip>
                                    <AddressSheet open={isAddressSheetOpen} onClose={() => setIsAddressSheetOpen(false)} addresses={addressList}
                                                  onSelect={(selectedAddress) => {
                                                      setLead((prev) => ({ ...prev, address: selectedAddress }));
                                                      setIsAddressSheetOpen(false);
                                                      if (detailsError) setDetailsError("");
                                                  }}
                                    />
                                </>
                            ) : (
                                <Tooltip title={addressList?.length > 0 || lead.postcode.trim() === "" ? "" : "Enter a valid postcode on the previous step to see addresses."}
                                         arrow placement="bottom" variant="solid" color={addressList?.length > 0 || lead.postcode.trim() === "" ? undefined : "danger"}>
                                    <Select
                                        size="lg" listboxOpen={isAddressSelectOpen} onOpenChange={(isOpen) => setIsAddressSelectOpen(isOpen)}
                                        onListboxOpenChange={(isOpen) => setIsAddressSelectOpen(isOpen)}
                                        disabled={addressList?.length === 0} required value={lead.address}
                                        slotProps={{ listbox: { sx: { fontSize: 16, maxHeight: 200, overflowY: "auto" } } }}
                                        onChange={(e, newValue) => {
                                            setLead((prev) => ({ ...prev, address: (newValue as string) || "" }));
                                            if (detailsError) setDetailsError("");
                                        }}
                                        placeholder="Your Address..."
                                        sx={{ background: "#fff", width: "100%", fontSize: 16 }} startDecorator={<Home />}
                                    >
                                        {addressList?.map((addressItem: Address, index: number) => (
                                            <Option key={index} value={addressItem.address}>{addressItem.address}</Option>
                                        ))}
                                    </Select>
                                </Tooltip>
                            )}

                            {detailsError && <FormHelperText sx={{ color: "danger.500" }}>{detailsError}</FormHelperText>}
                            <Button color="success" onClick={nextStep}
                                    sx={{ fontSize: '18px', mt: 1, py: 1.5, borderRadius: 8, width: "100%", fontWeight: 600 }}>
                                Continue
                            </Button>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => {
                                    setDetailsError("");
                                    setIsAddressSelectOpen(false);
                                    setIsAddressSheetOpen(false);
                                    setStep(1);
                                }}
                                sx={{
                                    alignSelf: "center",
                                    fontSize: "13px",
                                    color: "primary.500",
                                    textDecoration: "underline",
                                }}
                            >
                                Change postcode
                            </Link>
                        </Box>
                    )}

                    {/* --- NEW: CONDITIONAL TIMELINE STEP --- */}
                    {fbclid && step === 3 && (
                        <Box sx={{ px: '12px', display: "flex", flexDirection: "column", gap: 2 }}>
                            <Typography level="body-md" sx={{ color: "primary.500", fontWeight: 600 }}>
                                How soon are you looking to have your roof cleaned?
                            </Typography>
                            <Select
                                size="lg"
                                required
                                value={lead.timeline}
                                onChange={(e, newValue) => {
                                    setLead((prev) => ({ ...prev, timeline: (newValue as string) || "" }));
                                    if (detailsError) setDetailsError("");
                                }}
                                placeholder="Select a timeframe..."
                                sx={{ background: "#fff", width: "100%", fontSize: 16 }}
                                startDecorator={<CalendarMonth />}
                            >
                                <Option value="ASAP">ASAP</Option>
                                <Option value="Within 2 weeks">Within 2 weeks</Option>
                                <Option value="Within 1 month">Within 1 month</Option>
                                <Option value="Just Looking For Quotes">Just Looking For Quotes</Option>
                            </Select>
                            <Typography level="body-md" sx={{ color: "primary.500", fontWeight: 600 }}>
                                Why are you looking to get your roof cleaned?
                            </Typography>
                            <Select
                                size="lg"
                                required
                                value={lead.roofCleanReason}
                                onChange={(e, newValue) => {
                                    setLead((prev) => ({ ...prev, roofCleanReason: (newValue as string) || "" }));
                                    if (detailsError) setDetailsError("");
                                }}
                                placeholder="Select a reason..."
                                sx={{ background: "#fff", width: "100%", fontSize: 16 }}
                                startDecorator={<Savings />}
                            >
                                <Option value="Moss/algae is visible">Moss/algae is visible</Option>
                                <Option value="Roof looks dirty">Roof looks dirty</Option>
                                <Option value="Insurance/home maintenance">Insurance/home maintenance</Option>
                                <Option value="Selling or improving the property">Selling or improving the property</Option>
                                <Option value="Just curious about price">Just curious about price</Option>
                            </Select>
                            {detailsError && <FormHelperText sx={{ color: "danger.500" }}>{detailsError}</FormHelperText>}
                            <Button color="success" onClick={nextStep}
                                    sx={{ fontSize: '18px', mt: 1, py: 1.5, borderRadius: 8, width: "100%", fontWeight: 600 }}>
                                Continue
                            </Button>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => {
                                    setDetailsError("");
                                    setStep(1);
                                }}
                                sx={{
                                    alignSelf: "center",
                                    fontSize: "13px",
                                    color: "primary.500",
                                    textDecoration: "underline",
                                }}
                            >
                                Change postcode
                            </Link>
                        </Box>
                    )}


                    {/* --- FINAL STEP: CONTACT DETAILS --- */}
                    {step === totalSteps && (
                        <Box sx={{ px: '12px', display: "flex", flexDirection: "column", gap: 2 }}>
                            <Input
                                size="lg"
                                sx={{ background: "#fff" }}
                                required
                                value={lead.name}
                                startDecorator={<Person2 />}
                                onChange={(e) => setLead((prev) => ({ ...prev, name: e.target.value }))}
                                placeholder="Your Full Name..."
                            />
                            <Input
                                type="email"
                                inputMode="email"
                                size="lg"
                                sx={{ background: "#fff" }}
                                required
                                value={lead.eMail}
                                startDecorator={<Email />}
                                onChange={(e) => setLead((prev) => ({ ...prev, eMail: e.target.value }))}
                                placeholder="Your Email..."
                            />
                            <Input
                                autoComplete="tel"
                                name="phone"
                                type="tel"
                                inputMode="tel"
                                required
                                size="lg"
                                sx={{ background: "#fff" }}
                                value={lead.phone}
                                startDecorator={<Phone />}
                                onChange={(e) => {
                                    setLead((prev) => ({ ...prev, phone: e.target.value }));
                                    if (phoneError) setPhoneError("");
                                }}
                                onBlur={() => {
                                    if (lead.phone && isValidUkPhoneNumber(lead.phone)) {
                                        setLead((prev) => ({ ...prev, phone: formatUkPhoneNumber(prev.phone) }));
                                    }
                                }}
                                placeholder="Your Number..."
                                error={!!phoneError}
                            />
                            {phoneError && <FormHelperText sx={{ color: "danger.500" }}>{phoneError}</FormHelperText>}
                            <Button
                                color="success"
                                type="submit"
                                loading={loadingBookingsPage}
                                sx={{
                                    fontSize: '18px',
                                    mt: 1,
                                    py: 1.5,
                                    borderRadius: 8,
                                    width: "100%",
                                    fontWeight: 600,
                                }}
                            >
                                Send Me My Quote
                            </Button>
                        </Box>
                    )}
                    {isMobile ? (
                        <Box sx={{marginLeft: '12px', marginRight: '12px', marginBottom: '10px'}}>
                            <Typography variant="soft"  sx={{pl: 0, pr:0, ml: 0, mr: 0, textAlign: 'center', width: '100%', boxSizing: 'border-box', fontSize: '18px', fontWeight: 500}} level="h3" color={"success"}>Including a FREE Full Exterior <br/>Clean Worth £400</Typography>
                        </Box>
                    ) : (
                        <CardOverflow variant="soft" sx={{ mt: 2 }}>
                            <Divider inset="context" />
                            <CardContent sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 1.5 }}>
                                <Typography
                                    startDecorator={<Savings color="success" />}
                                    level="body-sm"
                                    sx={{ fontWeight: 600, color: "success.800" }}
                                >
                                    Free <Typography component="span" color="success" sx={{ fontWeight: 700, mx: 0.5 }}>£400</Typography>
                                    Exterior Clean Included
                                </Typography>
                            </CardContent>
                        </CardOverflow>
                    )}

                </Card>
            </form>
        </>
    );
}